import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AffiliateLink {
  affiliateId: string;
  linkCode: string;
  uniqueLink: string;
}

export interface AffiliateStats {
  referralsSubmitted: number;
  referralsActive: number;
  referralsPaidOut: number;
  totalEarned: number;
}

export interface CsvUploadSummary {
  queued: number;
  matched_existing: number;
  duplicate_in_batch: number;
}

export interface LeadRow {
  email: string;
  vendor_name?: string;
  contact_person?: string;
  category?: string;
  website?: string;
  phone?: string;
}

export interface PayoutLedgerEntry {
  id: string;
  spendAmount: string;
  payoutAmount: string;
  status: 'pending' | 'approved' | 'paid' | 'held_fraud' | 'reversed';
  calculatedAt: string;
  paidAt: string | null;
}

export interface PayoutAccountSnapshot {
  type: 'individual' | 'company' | null;
  displayName: string | null;
  detailsSubmitted: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  currentlyDue: string[];
}

export interface PayoutOnboardingStatus {
  agreementAccepted: boolean;
  agreementAcceptedAt: string | null;
  agreementVersion: string | null;
  currentAgreementVersion: string;
  payoutAccountConnected: boolean;
  w9Status: 'pending' | 'collected' | 'verified';
  payoutAccount: PayoutAccountSnapshot | null;
  taxProfileComplete: boolean;
}

export type TaxpayerType = 'individual' | 'business';

export type TaxClassification =
  | 'individual'
  | 'sole_proprietor'
  | 'llc_disregarded'
  | 'llc_partnership'
  | 'llc_corp'
  | 's_corp'
  | 'c_corp'
  | 'partnership';

export interface TaxProfileInput {
  taxpayerType: TaxpayerType;
  legalName: string;
  businessName?: string;
  taxClassification?: TaxClassification;
  tin: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface TaxProfileView {
  id: string;
  taxpayerType: TaxpayerType;
  legalName: string;
  businessName: string | null;
  taxClassification: string;
  tinLast4: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
  certifiedAt: string;
  certifiedIp: string | null;
  certificationVersion: string;
}

export interface AdminPayoutEntry {
  id: string;
  affiliateId: string;
  spendAmount: string;
  payoutAmount: string;
  status: 'pending' | 'approved' | 'paid' | 'held_fraud' | 'reversed';
  calculatedAt: string;
  paidAt: string | null;
  affiliate?: {
    id: string;
    userTypeLabel: string;
    w9Status: 'pending' | 'collected' | 'verified';
    taxProfile: { legalName: string; businessName: string | null } | null;
  };
}

@Injectable({ providedIn: 'root' })
export class AffiliateService {
  private readonly apiUrl = environment.affiliateApiUrl;

  /** Whether the current user has already registered as an affiliate. Null = not yet checked. */
  readonly isAffiliate = signal<boolean | null>(null);

  constructor(private readonly http: HttpClient) {}

  /** Registers the current user as an affiliate (any authenticated user may call this once). */
  becomeAffiliate(userTypeLabel: string): Observable<{ id: string; linkCode: string; uniqueLink: string }> {
    return this.http
      .post<{ id: string; linkCode: string; uniqueLink: string }>(`${this.apiUrl}/affiliates`, { userTypeLabel })
      .pipe(
        tap(() => this.isAffiliate.set(true)),
        catchError(this.handleError)
      );
  }

  /** Fetches the current user's affiliate link. 404s (not yet an affiliate) are surfaced as-is. */
  getMyLink(): Observable<AffiliateLink> {
    return this.http.get<AffiliateLink>(`${this.apiUrl}/affiliates/me/link`).pipe(
      tap(() => this.isAffiliate.set(true)),
      catchError((err) => {
        if (err?.status === 404) {
          this.isAffiliate.set(false);
        }
        return this.handleError(err);
      })
    );
  }

  getMyStats(): Observable<AffiliateStats> {
    return this.http.get<AffiliateStats>(`${this.apiUrl}/affiliates/me/stats`).pipe(catchError(this.handleError));
  }

  getMyPayouts(): Observable<{ data: PayoutLedgerEntry[] }> {
    return this.http
      .get<{ data: PayoutLedgerEntry[] }>(`${this.apiUrl}/affiliates/me/payouts`)
      .pipe(catchError(this.handleError));
  }

  /** Accepts .csv, .xlsx, and .xls files — the backend detects the format by extension. */
  uploadCsvFile(affiliateId: string, file: File): Observable<{ message: string; summary: CsvUploadSummary }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .post<{ message: string; summary: CsvUploadSummary }>(
        `${this.apiUrl}/affiliates/${affiliateId}/csv-upload-file`,
        formData
      )
      .pipe(catchError(this.handleError));
  }

  /** Submits hand-typed or extracted rows through the same pipeline as a file upload. */
  submitRows(affiliateId: string, rows: LeadRow[]): Observable<{ message: string; summary: CsvUploadSummary }> {
    return this.http
      .post<{ message: string; summary: CsvUploadSummary }>(`${this.apiUrl}/affiliates/${affiliateId}/csv-upload`, {
        rows,
      })
      .pipe(catchError(this.handleError));
  }

  /** Downloads the bulk-upload template as an .xlsx file. */
  downloadTemplate(): Observable<Blob> {
    return this.http
      .get(`${this.apiUrl}/affiliates/template`, { responseType: 'blob' })
      .pipe(catchError(this.handleError));
  }

  /** Preview-only: scans pasted text and returns every valid-looking email found. No DB writes. */
  extractEmails(affiliateId: string, text: string): Observable<{ emails: string[]; count: number }> {
    return this.http
      .post<{ emails: string[]; count: number }>(`${this.apiUrl}/affiliates/${affiliateId}/extract-emails`, {
        text,
      })
      .pipe(catchError(this.handleError));
  }

  recordLinkClick(linkCode: string): Observable<{ tracked: boolean }> {
    return this.http
      .post<{ tracked: boolean }>(`${this.apiUrl}/affiliates/link-click`, { linkCode })
      .pipe(catchError(this.handleError));
  }

  /** Attributes the current authenticated user's vendor signup to the referral link they came through. */
  registerMyReferral(linkCode: string): Observable<{ success: boolean }> {
    return this.http
      .post<{ success: boolean }>(`${this.apiUrl}/affiliates/me/register-referral`, { linkCode })
      .pipe(catchError(this.handleError));
  }

  getPayoutOnboardingStatus(): Observable<PayoutOnboardingStatus> {
    return this.http
      .get<PayoutOnboardingStatus>(`${this.apiUrl}/affiliates/me/payout-onboarding`)
      .pipe(catchError(this.handleError));
  }

  acceptAgreement(): Observable<{ agreementAcceptedAt: string; agreementVersion: string }> {
    return this.http
      .post<{ agreementAcceptedAt: string; agreementVersion: string }>(`${this.apiUrl}/affiliates/me/agreement/accept`, {})
      .pipe(catchError(this.handleError));
  }

  createPayoutOnboardingLink(returnUrl: string, refreshUrl: string): Observable<{ url: string }> {
    return this.http
      .post<{ url: string }>(`${this.apiUrl}/affiliates/me/payout-onboarding/link`, { returnUrl, refreshUrl })
      .pipe(catchError(this.handleError));
  }

  /** Submits the affiliate's tax profile for 1099 compliance. The raw TIN is never echoed back. */
  submitTaxProfile(input: TaxProfileInput): Observable<TaxProfileView> {
    return this.http
      .post<TaxProfileView>(`${this.apiUrl}/affiliates/me/tax-profile`, input)
      .pipe(catchError(this.handleError));
  }

  /** Returns null if no tax profile has been submitted yet. */
  getMyTaxProfile(): Observable<TaxProfileView | null> {
    return this.http
      .get<TaxProfileView | null>(`${this.apiUrl}/affiliates/me/tax-profile`)
      .pipe(catchError(this.handleError));
  }

  /** Admin-only: lists payout ledger entries across all affiliates, optionally filtered by status. */
  listPayoutsForAdmin(status?: string): Observable<{ data: AdminPayoutEntry[] }> {
    let url = `${this.apiUrl}/affiliates/admin/payouts`;
    if (status) {
      url += `?status=${encodeURIComponent(status)}`;
    }
    return this.http.get<{ data: AdminPayoutEntry[] }>(url).pipe(catchError(this.handleError));
  }

  /** Admin-only: transitions a payout entry to a new status. */
  updatePayoutStatus(payoutId: string, status: string): Observable<AdminPayoutEntry> {
    return this.http
      .patch<AdminPayoutEntry>(`${this.apiUrl}/affiliates/admin/payouts/${payoutId}`, { status })
      .pipe(catchError(this.handleError));
  }

  /** Admin-only: downloads the 1099-NEC export CSV for the given tax year (defaults to current year). */
  export1099(year?: number): Observable<HttpResponse<Blob>> {
    let url = `${this.apiUrl}/affiliates/admin/1099-export`;
    if (year) {
      url += `?year=${year}`;
    }
    return this.http
      .get(url, { responseType: 'blob', observe: 'response' })
      .pipe(catchError(this.handleError));
  }

  private handleError(error: any): Observable<never> {
    const message = error?.error?.message || error?.message || 'An unexpected error occurred.';
    return throwError(() => new Error(message));
  }
}
